/**
 * L'extraction des paires d'une comparaison rétrospective — la lecture de
 * paillasse face à celle d'IRIS, bandelette par bandelette.
 *
 * Sorti de `RetrospectiveComparison` le jour où le registre d'évaluation a eu
 * besoin des mêmes paires pour agréger PLUSIEURS cas : si la fiche d'un cas et
 * le registre construisaient chacun leurs paires, un écart de logique — une
 * bandelette comptée ici et pas là — ferait deux chiffres d'accord différents
 * pour les mêmes lectures, et c'est le genre de contradiction qu'un relecteur
 * ne pardonne pas. Une seule extraction, deux affichages.
 *
 * L'agrégation elle-même reste à `agreementStats` : ces fonctions ne font que
 * transformer deux instantanés figés en observations appariées.
 */

/** Une lecture d'un côté de la comparaison. `intensities` n'existe que du
 *  côté IRIS et seulement sur les cas figés depuis novembre 2026 : le
 *  biologiste, lui, ne gradue pas ses bandes — il les voit ou ne les voit
 *  pas. C'est donc une grandeur de la lecture machine, utilisable pour
 *  stratifier ce qu'IRIS a retenu, jamais ce que la paillasse a vu. */
export interface SideReading {
  bands?: string[]
  conclusion?: string | null
  intensities?: Record<string, number>
}

export type ComparisonCell = 'accord' | 'manquee' | 'ajoutee'

export interface ComparisonRow {
  id: number
  /** `irisRatio` : intensité relative au témoin de la bande, du côté IRIS,
   *  quand l'instantané la porte. Absente sur les cas figés avant novembre
   *  2026 et sur les bandes qu'IRIS n'a pas retenues. */
  cells: Array<{ band: string; state: ComparisonCell | null; irisRatio?: number }>
  benchVerdict: string | null
  irisVerdict: string | null
  verdictAgrees: boolean
  divergences: number
}

/**
 * Les lignes de comparaison d'un cas : une par bandelette comparable.
 *
 * L'intersection, pas l'union : une plaque multitest se fige recherche par
 * recherche, et une bandelette dont seule la lecture de paillasse existe
 * n'est pas encore comparable — l'afficher avec un IRIS vide ferait lire
 * une attente comme un désaccord.
 */
export function comparisonRows(
  benchBlots: Record<string, SideReading>,
  irisBlots: Record<string, SideReading>,
  bandNames: string[],
): ComparisonRow[] {
  const ids = Object.keys(benchBlots)
    .filter((id) => irisBlots[id])
    .map(Number)
    .filter((id) => !Number.isNaN(id))
    .sort((a, b) => a - b)

  return ids.map((id) => {
    const bench = new Set(benchBlots[String(id)]?.bands ?? [])
    const iris = new Set(irisBlots[String(id)]?.bands ?? [])
    const irisRatios = irisBlots[String(id)]?.intensities
    const cells = bandNames.map((band): { band: string; state: ComparisonCell | null; irisRatio?: number } => {
      const b = bench.has(band)
      const i = iris.has(band)
      // L'intensité ne se transporte que là où IRIS a retenu la bande : sur
      // une bande qu'il n'a pas vue, il n'y a pas de mesure à rapporter.
      const irisRatio = i ? irisRatios?.[band] : undefined
      if (b === i) return { band, state: b ? 'accord' : null, irisRatio }
      // Nommées du point de vue d'IRIS : ce qu'il a laissé passer, ce qu'il a
      // vu en trop. C'est l'angle utile pour corriger la détection.
      return { band, state: b ? 'manquee' : 'ajoutee', irisRatio }
    })
    const benchVerdict = benchBlots[String(id)]?.conclusion ?? null
    const irisVerdict = irisBlots[String(id)]?.conclusion ?? null
    return {
      id,
      cells,
      benchVerdict,
      irisVerdict,
      verdictAgrees: benchVerdict != null && benchVerdict === irisVerdict,
      divergences: cells.filter((c) => c.state === 'manquee' || c.state === 'ajoutee').length,
    }
  })
}

/**
 * Deux accords, deux dénominateurs : les conclusions se comptent par
 * bandelette, les bandes par bandelette ET par bande. Le second est le plus
 * nourri, et c'est souvent le seul qui ait assez d'observations pour qu'un
 * kappa veuille dire quelque chose sur un cas isolé.
 */
export const verdictPairsOf = (rows: ComparisonRow[]): Array<[boolean, boolean]> =>
  rows
    .filter((r) => r.benchVerdict != null && r.irisVerdict != null)
    .map((r) => [r.benchVerdict === 'POSITIF', r.irisVerdict === 'POSITIF'])

export const bandPairsOf = (rows: ComparisonRow[]): Array<[boolean, boolean]> =>
  rows.flatMap((r) =>
    r.cells.map((c) => [
      c.state === 'accord' || c.state === 'manquee',
      c.state === 'accord' || c.state === 'ajoutee',
    ] as [boolean, boolean]),
  )
